import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import PaginationModule from './PaginationModule';
import ResponsiveDataList from '../../Table/ResponsiveDataList';
import noop from '../../../util/noop';
import useContainerProps from '../../../hooks/useContainerProps';
import Storage from '../../../util/Storage';

/** renders a list module intended for use inside a section */
const ListModule = props => {
  props = useContainerProps(props);

  const { activeSort, activePage = 1, collection = [], fields = [{ name: 'name' }],
    keyField, loadCollection = noop, onPageChange = noop, options = {}, onSortChange = noop,
    setActivePage = noop, setActiveSort = noop } = props;

  useEffect(() => {
    loadCollection()
  }, [activePage, activeSort, Storage.pageSize])

  const handlePageChange = page => {
    onPageChange(page);
    setActivePage(page);
  }

  const handleSortChange = (sortValue, sortDirection) => {
    if (activeSort &&
      sortValue === activeSort.value &&
      sortDirection === activeSort.direction) {
      return;
    }
    setActiveSort({
      value: sortValue,
      direction: sortDirection
    });
    setActivePage(1);
    onSortChange(sortValue, sortDirection);
    onPageChange(1);
  }

  useEffect(() => {
    if (!activeSort) {
      const defaultSortField = fields.find((field) => field.defaultSort);
      if (defaultSortField) {
        const sortValue = defaultSortField.name;
        const sortDirection = defaultSortField.defaultSort === true ?
          'asc' : defaultSortField.defaultSort;
        handleSortChange(sortValue, sortDirection);
      }
    }
  }, []);

  const tableOptions = Object.assign({
    bordered: false,
    hover: true,
    onSortChange: handleSortChange,
    sortName: activeSort && activeSort.value,
    sortOrder: activeSort && activeSort.direction,
    striped: false
  }, options);

  return (
    <PaginationModule
      {...props}
      collection={collection}
      onPageChange={handlePageChange}
      setActivePage={setActivePage}
      loadCollection={noop}
    >
      <ResponsiveDataList
        {...props}
        data={collection}
        fields={fields}
        keyField={keyField}
        options={tableOptions}
      />
    </PaginationModule>
  )
};

ListModule.propTypes = {
  activePage: PropTypes.number,
  /** object/array/string that defines the sort order */
  activeSort: PropTypes.any,
  collection: PropTypes.array,
  /** define how the table rows are rendered
   *  (passed to ResponsiveDataList -> Table -> BootstrapTable component)
   *  documentation of additional BootstrapTable column props can be found at
   *  https://react-bootstrap-table.github.io/react-bootstrap-table2/docs/column-props.html */
  fields: PropTypes.arrayOf(PropTypes.shape({
    /** if set to true, then the list will be sorted by this column by default */
    defaultSort: PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.string
    ])
  })),
  /** collection prop used as the key for each list item */
  keyField: PropTypes.string,
  loadCollection: PropTypes.func,
  onPageChange: PropTypes.func,
  /** callback(sortColumn, sortDirection) when page is changed */
  onSortChange: PropTypes.func,
  /** any table options are passed to ResponsiveDataList -> Table component */
  options: PropTypes.object,
  setActivePage: PropTypes.func,
  setActiveSort: PropTypes.func
};

ListModule.defaultProps = {
  keyField: 'id'
};

export default ListModule;
